import React, { Suspense, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { EcdAppConfigs } from '../global/models/configuration-models';
import { getBasePath } from '../global/helpers/routing';
import { withLayout } from '../global/components/layout/with-layout';
import { EcdStates } from './ecd-model';
import { getEcdEpiProps } from '../global/ducks/epi-api';
import { setCurrentLanguage } from '../../TCSMApp/global/ducks/globalization';
import { LayoutModels } from '../global/components/layout/layout-models';
import { EcdRoutes } from '../global/constants/routes';
import { ExternalLinkModal } from '../global/components/external-link-modal';
import { ErrorBoundaryApp } from '../../TCSMApp/global/components/error-boundary';

// eslint-disable-next-line @typescript-eslint/naming-convention
const PageFactoryWrapper = withLayout(LayoutModels.FULL_WIDTH)(React.lazy(() => import('../global/components/page-factory')));

// eslint-disable-next-line @typescript-eslint/naming-convention
const ClergyAppWrapper = withLayout(LayoutModels.FULL_WIDTH)(React.lazy(() => import('../global/components/clergy/app-clergy')));

// eslint-disable-next-line @typescript-eslint/naming-convention
const ParishAppWrapper = withLayout(LayoutModels.FULL_WIDTH)(React.lazy(() => import('../global/components/parish/app-parish')));

interface Props {
	appConfig: EcdAppConfigs
}
export function EcdApp(props: Props) {
	const { appConfig } = props;
	const dispatch = useDispatch();
	const location = useLocation()?.pathname;

	const siteSettingsLoaded = useSelector<EcdStates, boolean>(state => state?.epiProps?.siteSettingsState?.siteSettingsLoaded);
	const epiPropertiesLoaded = siteSettingsLoaded;

	useEffect(() => {
		if (!epiPropertiesLoaded) {
			dispatch(getEcdEpiProps(dispatch, appConfig.contentId, {}, appConfig.currentCulture, false));
			dispatch(getEcdEpiProps(dispatch, appConfig.siteSettingsId, {}, appConfig.currentCulture, true));
			dispatch(setCurrentLanguage(appConfig.currentCulture));
		}
	}, [epiPropertiesLoaded]);

	useEffect(() => {
		if (location) {
			window.scrollTo(0, 0);
		}
	}, [location]);

	const basePath = getBasePath(appConfig.currentCulture);

	if (!epiPropertiesLoaded) {
		return <LoadingCircle />;
	}

	return (
		<main id="main">
			<ErrorBoundaryApp>
				<ExternalLinkModal />
				<Suspense fallback={<LoadingCircle />}>
					<Switch>
						<Route exact path={`${basePath}`}>
							<Redirect to={{ pathname: `${basePath}/${EcdRoutes.Clergy}/` }} />
						</Route>
						<Route path={`${basePath}/${EcdRoutes.Clergy}`}>
							<ClergyAppWrapper basePath={basePath} />
						</Route>
						<Route path={`${basePath}/${EcdRoutes.Parish}`}>
							<ParishAppWrapper />
						</Route>
						<Route path={`${basePath}/*`}>
							<PageFactoryWrapper />
						</Route>
						<Route>
							<>
								No route
							</>
						</Route>
					</Switch>
				</Suspense>
			</ErrorBoundaryApp>
		</main>
	);
}